import {
  Button,
  Card,
  Col,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Spin,
  Checkbox,
} from 'antd';
import { useNavigate } from 'react-router-dom';
import { useRequest } from 'ahooks';
import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  getIndustryChains,
  getIndustryChainBelongs,
  getLoanBanks,
  addMajorFinancing,
} from '@/services/supplyChainFinance';
import AuthorizationInfo from '@/components/FormInfo/AuthorizationInfo';
import './index.less';

const formItemLayout = {
  labelCol: {
    sm: { span: 8 },
  },
  wrapperCol: {
    sm: { span: 16 },
  },
};

export default function Application() {
  const [form] = Form.useForm();
  const Navigate = useNavigate();
  const { userType } = useSelector((state) => state);
  const user = useSelector((state) => state.userInfo);
  const [authInfoModal, setAuthInfoModal] = useState(false); // 授权协议内容
  const [industryChainList, setIndustryChainList] = useState([]); // 产业链列表
  const [projectList, setProjectList] = useState([]); // 项目列表
  const [bankList, setBankList] = useState([]); // 银行列表
  const [industryChainDisabled, setIndustryChainDisabled] = useState(false); // 产业链Select是否禁用
  const [projectNameDisabled, setProjectNameDisabled] = useState(false); // 项目名称Select是否禁用
  const [pageLoading, setPageLoading] = useState(false); // 页面加载状态

  const { loading: submitLoading, run: submitRun } = useRequest(addMajorFinancing, {
    manual: true,
    onSuccess() {
      Navigate('/result');
    },
  });

  // 是否选项
  const yesNoOptions = [
    { label: '是', value: 0 },
    { label: '否', value: 1 },
  ];

  function onFinish(values) {
    // 根据 billionAgriProjectId 找到对应的项目名称
    const selectedProject = projectList.find(
      (project) => project.billionAgriProjectId === values.billionAgriProjectId,
    );
    const projectName = selectedProject ? selectedProject.projectName : '';

    const obj = {
      ...values,
      applicantId: user.id,
      applicantName: userType === 2 ? user.realName : user.displayName,
      applicantNo: userType === 2 ? user.idcardNo : user.displayCode,
      projectName,
    };
    submitRun(obj);
  }

  function checkAuthorize(_, value) {
    if (!value) {
      return Promise.reject(new Error('请勾选同意并接受'));
    }
    return Promise.resolve();
  }

  // 处理项目名称变化
  const handleProjectChange = async (projectId) => {
    if (projectId) {
      try {
        // 清空银行列表
        setBankList([]);
        form.setFieldsValue({ orgIdLists: undefined });

        // 获取银行列表
        const banks = await getLoanBanks(projectId);
        if (banks && banks.length > 0) {
          const bankOptions = banks.map((item) => ({
            label: item.orgName,
            value: item.orgId,
          }));
          setBankList(bankOptions);
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('获取银行列表失败:', error);
      }
    } else {
      // 清空银行列表
      setBankList([]);
      form.setFieldsValue({ orgIdLists: undefined });
    }
  };

  // 根据产业链加载项目列表
  const loadProjectsByIndustryChain = async (industryChainBelongs) => {
    try {
      // 获取该产业链对应的项目列表
      const projects = await getIndustryChainBelongs(industryChainBelongs);

      if (projects && projects.length > 0) {
        const projectOptions = projects.map((item) => ({
          label: item.projectName,
          value: item.billionAgriProjectId,
          projectName: item.projectName,
          billionAgriProjectId: item.billionAgriProjectId,
        }));
        setProjectList(projectOptions);

        // 如果只有一个项目，自动选中并禁用
        if (projects.length === 1) {
          setProjectNameDisabled(true);
          const projectId = projects[0].billionAgriProjectId;
          form.setFieldsValue({
            billionAgriProjectId: projectId,
          });
          // 自动加载该项目的银行列表
          await handleProjectChange(projectId);
        } else {
          // 如果有多个项目，启用选择
          setProjectNameDisabled(false);
        }
      } else {
        setProjectList([]);
        setProjectNameDisabled(false);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('加载项目列表失败:', error);
      setProjectList([]);
      setProjectNameDisabled(false);
    }
  };

  // 加载产业链列表
  const loadIndustryChains = async () => {
    try {
      setPageLoading(true);
      // 获取产业链列表
      const industryChains = await getIndustryChains();

      if (industryChains && industryChains.length > 0) {
        // 处理产业链列表
        const chainOptions = industryChains.map((item) => ({
          label: item.industryChainBelongs,
          value: item.industryChainBelongs,
        }));
        setIndustryChainList(chainOptions);

        // 如果只有一个产业链，自动选中并禁用，并自动加载项目列表
        if (industryChains.length === 1) {
          setIndustryChainDisabled(true);
          const industryChain = industryChains[0].industryChainBelongs;
          form.setFieldsValue({
            industryChainBelongs: industryChain,
          });
          // 自动加载该产业链的项目列表
          await loadProjectsByIndustryChain(industryChain);
        }
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('加载产业链列表失败:', error);
    } finally {
      setPageLoading(false);
    }
  };

  // 处理所属产业链变化
  const handleIndustryChainChange = async (industryChainBelongs) => {
    // 清空项目名称和银行列表
    form.setFieldsValue({
      billionAgriProjectId: undefined,
      orgIdLists: undefined,
    });
    setProjectList([]);
    setBankList([]);
    setProjectNameDisabled(false);

    if (industryChainBelongs) {
      await loadProjectsByIndustryChain(industryChainBelongs);
    }
  };

  // 页面加载时获取数据
  useEffect(() => {
    loadIndustryChains();
  }, []);

  return (
    <div className="supply-chain-finance__info">
      <div className="content-width">
        <div className="supply-chain-finance__info-title">融资申请</div>
        <div className="margin-top20">
          <Spin spinning={pageLoading}>
            <Form
              {...formItemLayout}
              form={form}
              name="form"
              className="form"
              onFinish={onFinish}
            >
              <Card title="融资需求" className="form-info__box">
                <Row>
                  <Col span={12}>
                    <Form.Item
                      name="industryChainBelongs"
                      label="所属产业链"
                      rules={[
                        { required: true, message: '请选择所属产业链' },
                      ]}
                    >
                      <Select
                        placeholder="请选择所属产业链"
                        options={industryChainList}
                        disabled={industryChainDisabled}
                        onChange={handleIndustryChainChange}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name="billionAgriProjectId"
                      label="项目名称"
                      rules={[
                        { required: true, message: '请选择项目名称' },
                      ]}
                    >
                      <Select
                        placeholder="请选择项目名称"
                        options={projectList}
                        disabled={projectNameDisabled}
                        onChange={handleProjectChange}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name="financingAmount"
                      label="融资金额"
                      rules={[
                        { required: true, message: '请输入融资金额' },
                      ]}
                    >
                      <InputNumber
                        placeholder="请输入融资金额"
                        addonAfter="万元"
                        style={{ width: '100%' }}
                        min={0}
                        precision={2}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name="capitalTerm"
                      label="资金使用期限"
                      rules={[
                        { required: true, message: '请输入资金使用期限' },
                      ]}
                    >
                      <InputNumber
                        placeholder="请输入资金使用期限"
                        addonAfter="月"
                        style={{ width: '100%' }}
                        min={0}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name="orgIdLists"
                      label="申请贷款银行"
                      rules={[
                        { required: true, message: '请选择申请贷款银行' },
                      ]}
                    >
                      <Select
                        mode="multiple"
                        placeholder="请选择申请贷款银行"
                        options={bankList}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name="financingStaus"
                      label="是否可提供抵押"
                      rules={[
                        { required: true, message: '请选择是否可提供抵押' },
                      ]}
                    >
                      <Select
                        placeholder="请选择是否可提供抵押"
                        options={yesNoOptions}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={24}>
                    <Form.Item
                      labelCol={{ sm: { span: 4 } }}
                      wrapperCol={{ sm: { span: 20 } }}
                      name="loanUsa"
                      label="资金详细用途"
                      rules={[
                        { required: true, message: '请输入资金详细用途' },
                        { whitespace: true },
                      ]}
                    >
                      <Input.TextArea
                        maxLength={1000}
                        showCount
                        placeholder="请输入资金详细用途"
                        rows={4}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </Card>
              <div className="authorize">
                <Form.Item
                  required
                  name="authorize"
                  wrapperCol={{ span: 24 }}
                  valuePropName="checked"
                  rules={[
                    { validator: checkAuthorize },
                  ]}
                >
                  <Checkbox
                    onChange={(e) => {
                      form.setFieldsValue({ authorize: false });
                      if (e.target.checked) {
                        setAuthInfoModal(true);
                      }
                    }}
                  >
                    您需要同意并接受
                    <Button type="link" className="authorize-name" onClick={() => setAuthInfoModal(true)}>《直通车平台业务授权协议》</Button>
                  </Checkbox>
                </Form.Item>
              </div>
              <div className="submit-btn-box">
                <Button type="primary" htmlType="submit" loading={submitLoading}>
                  提交申请
                </Button>
              </div>
            </Form>
          </Spin>
          <Modal
            title="授权协议查看"
            open={authInfoModal}
            width={900}
            footer={
              [
                <Button key="skip" onClick={() => setAuthInfoModal(false)}>
                  取消
                </Button>,
                <Button
                  type="primary"
                  key="confirm"
                  onClick={() => {
                    setAuthInfoModal(false);
                    form.setFieldsValue({ authorize: true });
                  }}
                >
                  确认
                </Button>,
              ]
            }
            onCancel={() => setAuthInfoModal(false)}
          >
            <div dangerouslySetInnerHTML={{ __html: AuthorizationInfo }} style={{ maxHeight: 600, overflowY: 'scroll' }} />
          </Modal>
        </div>
      </div>
    </div>
  );
}
